import { Component, input, output, computed, ChangeDetectionStrategy } from '@angular/core';
import { FormStatus } from '@formrails/form-builder/data-access';
import { PromotionDto } from '@formrails/shared/data-access';
import { FormType, FORM_TYPE_OPTIONS } from '@formrails/shared/ui';

/**
 * Component for displaying and editing form metadata (title, description, version, type).
 * The status badge and archive action live in the toolbar.
 * This is a presentational component that emits events for changes.
 * Uses Angular signals for reactive inputs and outputs.
 */
@Component({
  selector: 'app-form-header',
  standalone: true,
  templateUrl: './form-header.component.html',
  styleUrl: './form-header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormHeaderComponent {
  // Signal inputs
  readonly formTitle = input('');
  readonly formDescription = input('');
  readonly formVersion = input(0);
  readonly formType = input<FormType | null>(null);
  readonly formStatus = input<FormStatus>(FormStatus.Draft);
  /** How far this version has travelled towards the next environment. */
  readonly promotion = input<PromotionDto | null>(null);
  /**
   * Where a promotion from here lands — sandbox from CI, production from
   * sandbox. Used only until a promotion records where it actually went.
   */
  readonly promotionTarget = input<string | null>(null);
  readonly isReadOnly = input(false);

  /**
   * Get the form type icon based on the current form type
   */
  readonly formTypeIcon = computed(() => {
    const type = this.formType();
    if (!type) return null;
    const option = FORM_TYPE_OPTIONS.find(opt => opt.type === type);
    return option ? option.icon : null;
  });

  /** The enum's values are lowercase ('published'), so label them explicitly. */
  readonly statusLabel = computed(() => {
    switch (this.formStatus()) {
      case FormStatus.Published: return 'Published';
      case FormStatus.Archived: return 'Archived';
      default: return 'Draft';
    }
  });

  readonly FormStatus = FormStatus;

  /** Nothing to show until a promotion has been asked for. */
  readonly promotionState = computed(() => {
    const state = this.promotion()?.state;
    return state && state !== 'None' ? state : null;
  });

  /** Where this version went, or is going, in prose. */
  readonly targetLabel = computed(() =>
    (this.promotion()?.targetEnvironment ?? this.promotionTarget() ?? 'production').toLowerCase()
  );

  readonly promotionLabel = computed(() => {
    switch (this.promotionState()) {
      case 'Requested': return 'Awaiting sign-off';
      case 'Approved': return 'Promoting';
      case 'Promoted': return `In ${this.targetLabel()}`;
      case 'Failed': return 'Promotion failed';
      default: return null;
    }
  });

  readonly promotionIcon = computed(() => {
    switch (this.promotionState()) {
      case 'Requested': return 'pi pi-clock';
      case 'Approved': return 'pi pi-send';
      case 'Promoted': return 'pi pi-check-circle';
      case 'Failed': return 'pi pi-exclamation-circle';
      default: return '';
    }
  });

  /**
   * What the chip says on hover: who asked, who signed off, when it landed, or
   * why it didn't.
   */
  readonly promotionDetail = computed(() => {
    const promotion = this.promotion();
    if (!promotion) return '';
    switch (promotion.state) {
      case 'Requested':
        return `Requested by ${promotion.requestedBy ?? 'someone'}${this.on(promotion.requestedOn)}`;
      case 'Approved':
        return `Signed off by ${promotion.approvedBy ?? 'someone'}${this.on(promotion.approvedOn)}`;
      case 'Promoted':
        return `In ${this.targetLabel()}${this.on(promotion.promotedOn)}`;
      case 'Failed':
        return promotion.error ?? 'The last attempt failed';
      default:
        return '';
    }
  });

  private on(date?: string): string {
    return date ? ` on ${new Date(date).toLocaleDateString()}` : '';
  }

  /** Human-readable label for the current form type (e.g. "User Profile"). */
  readonly formTypeLabel = computed(() => {
    const type = this.formType();
    if (!type) return null;
    const option = FORM_TYPE_OPTIONS.find(opt => opt.type === type);
    return option ? option.label : type;
  });

  // Signal outputs
  readonly titleChange = output<string>();
  readonly descriptionChange = output<string>();
  readonly versionChange = output<number>();

  onTitleChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.titleChange.emit(value);
  }

  onDescriptionChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.descriptionChange.emit(value);
  }

  onVersionChange(event: Event): void {
    const value = +(event.target as HTMLInputElement).value;
    this.versionChange.emit(value);
  }
}
